import { signS3Inventory } from "@api/SignS3";
import { useMutation, useQuery } from "@apollo/client";
import { CREATE_INVENTORY } from "@components/notes/edit/api/mutations";
import { faUser } from "@fortawesome/free-regular-svg-icons";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { GET_INVENTORIES } from "@graphql/queries/GetInventories";
import axios from "axios";
import mixpanel from "mixpanel-browser";
import moment from "moment";
import { useRef, useState } from "react";

import { AccountItem, AccountItemLine } from "./AccountItem";
import { useWatchUserWsEvent } from "../../hooks";
import { alert } from "../common/Alert.js";
import { BasicButton } from "../common/buttons/BasicButton";

const MAX_INVENTORY_SIZE_MB = 10;

export default function CompanionSettings() {
  const [createInventory] = useMutation(CREATE_INVENTORY);

  const [isUploading, setIsUploading] = useState(false);
  const { data, refetch } = useQuery(GET_INVENTORIES);

  useWatchUserWsEvent({
    onMessage: refetch,
    onConnect: refetch,
    eventName: "user_update",
  });

  const handleFileInputChange = async (e) => {
    const { files } = e.target;
    if (!files.length) return;
    const file = files[0];

    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > MAX_INVENTORY_SIZE_MB) {
      mixpanel.track("Inventory Upload Failed", {
        fileSizeMB,
        error: "File too large",
      });
      alert(
        "error",
        `File is too large (max: ${MAX_INVENTORY_SIZE_MB}MB)`,
      );
      return;
    }

    setIsUploading(true);

    try {
      const signS3Response = await signS3Inventory();

      const { data, storageObjectName } = signS3Response.data;
      const { fields } = data;

      const formData = new FormData();
      for (const [key, value] of Object.entries(fields)) {
        formData.append(key, value);
      }

      formData.append("file", file);
      formData.append("Content-Type", file.type);

      await axios.post(data.url, formData, {
        headers: {
          "Access-Control-Allow-Origin": true,
        },
      });

      await createInventory({
        variables: {
          storageObjectName,
        },
      });

      refetch();

      alert("success", "Inventory uploaded");
    } catch (error) {
      mixpanel.track("Inventory Upload Failed", {
        fileSizeMB,
        error: error.message,
      });
      alert(
        "error",
        "Failed to upload your inventory. Please try again.",
      );
    } finally {
      setIsUploading(false);
    }
  };

  const inventories = data?.user?.inventories || [];
  const fileInputRef = useRef(null);

  return (
    <AccountItem
      title="Companion"
      icon={<FontAwesomeIcon icon={faUser} size="lg" color="gray" />}
    >
      <div className="flex-1 flex mr-4 md:space-x-4">
        <div className="flex-1 divide-y dark:divide-gray-700">
          <div className="m-2">
            <BasicButton
              disabled={isUploading}
              onClick={() => {
                fileInputRef.current.click();
              }}
            >
              {!isUploading && <>Upload new inventory</>}

              {isUploading && (
                <>
                  Uploading...
                  <FontAwesomeIcon
                    className="ml-2 animate-spin"
                    icon={faSpinner}
                  />
                </>
              )}
            </BasicButton>
          </div>

          <input
            type="file"
            hidden
            ref={fileInputRef}
            accept="application/pdf"
            onChange={handleFileInputChange}
          />

          {inventories.map((inventory) => {
            const date = moment(inventory.createdAt).format(
              "MMMM Do YYYY, h:mma",
            );
            const diff = moment(inventory.createdAt).fromNow();

            return (
              <AccountItemLine
                key={inventory.inventoryUuid}
                title={`${date} (${diff})`}
              >
                {inventory.status}
                {inventory.status === "PROCESSING" && (
                  <FontAwesomeIcon
                    className="ml-2 animate-spin"
                    icon={faSpinner}
                  />
                )}
              </AccountItemLine>
            );
          })}
        </div>
      </div>
    </AccountItem>
  );
}
